import { useMemo } from 'react';
import { Contract, isObject, maybeNumber } from 'validata';
import { useObjectFlag } from '../components/flags';
import { DEFAULT_MAP_MAX_ZOOM, DEFAULT_MAP_MIN_ZOOM, MAP_ZOOM_CEILING, MAP_ZOOM_FLOOR } from '../constants';
import { MAP_ZOOM_LIMITS_FLAG } from '../feature-flags';
import { MapZoomLimits } from '../types';

interface MapZoomLimitsFlag {
  min?: number;
  max?: number;
}

const mapZoomLimitsContract: Contract<MapZoomLimitsFlag> = {
  min: maybeNumber(),
  max: maybeNumber(),
};

const mapZoomLimitsValidator = isObject(mapZoomLimitsContract);

const DEFAULT_ZOOM_LIMITS: MapZoomLimits = { minZoom: DEFAULT_MAP_MIN_ZOOM, maxZoom: DEFAULT_MAP_MAX_ZOOM };
let hasWarnedAboutInvalidZoomLimits = false;

const toZoom = (value: number | undefined, fallback: number): number => {
  if (value === undefined || !Number.isFinite(value)) return fallback;
  const snapped = Math.round(value * 2) / 2;
  return Math.min(Math.max(snapped, MAP_ZOOM_FLOOR), MAP_ZOOM_CEILING);
};

export const resolveMapZoomLimits = (flag: MapZoomLimitsFlag | undefined): MapZoomLimits => {
  if (!flag) return DEFAULT_ZOOM_LIMITS;
  const minZoom = toZoom(flag.min, DEFAULT_MAP_MIN_ZOOM);
  const maxZoom = toZoom(flag.max, DEFAULT_MAP_MAX_ZOOM);
  if (minZoom >= maxZoom) {
    if (!hasWarnedAboutInvalidZoomLimits) {
      console.warn(`Invalid feature flag: ${MAP_ZOOM_LIMITS_FLAG}. min (${minZoom}) must be below max (${maxZoom}).`);
      hasWarnedAboutInvalidZoomLimits = true;
    }
    return DEFAULT_ZOOM_LIMITS;
  }
  return { minZoom, maxZoom };
};

export const useMapZoomLimits = (): MapZoomLimits => {
  const flag = useObjectFlag(MAP_ZOOM_LIMITS_FLAG, mapZoomLimitsValidator);
  return useMemo(() => resolveMapZoomLimits(flag), [flag]);
};
